import { createSignal } from 'solid-js'
import { createStore } from 'solid-js/store'
import { defaults } from './scene/factory.ts'
import type { NodeId, Point, Rect } from './scene/types.ts'

export type ToolId = 'select' | 'pan' | 'rect' | 'ellipse' | 'line' | 'pen' | 'text'

/** Ephemeral editor state. None of this is saved with a project. */

const [activeTool, setActiveTool] = createSignal<ToolId>('select')
export { activeTool }

export function useTool(id: ToolId): void {
  setActiveTool(id)
}

/** Text node currently open for editing, if any. */
const [editingTextId, setEditingTextId] = createSignal<NodeId | null>(null)
export { editingTextId, setEditingTextId }

/** Marquee rect in world space while a band-select drag is in flight. */
const [marquee, setMarquee] = createSignal<Rect | null>(null)
export { marquee, setMarquee }

/** In-progress freehand stroke, world coordinates, flat [x,y,...]. */
export interface LiveStroke {
  points: number[]
  stroke: string
  strokeWidth: number
  /** Straight segments rather than the pen's smoothing. */
  polyline?: boolean
  closed?: boolean
  /** World point to ring as a close target, while the pointer is snapped to it. */
  closeAt?: Point | null
}
const [liveStroke, setLiveStroke] = createSignal<LiveStroke | null>(null)
export { liveStroke, setLiveStroke }

/** Space held: temporarily pans regardless of the active tool. */
const [spaceDown, setSpaceDown] = createSignal(false)
export { spaceDown, setSpaceDown }

const [viewportSize, setViewportSize] = createSignal({ w: 0, h: 0 })
export { viewportSize, setViewportSize }

/** Style applied to newly created nodes, and editable from the style panel. */
export const [style, setStyle] = createStore({
  fill: defaults.fill,
  stroke: defaults.stroke,
  strokeWidth: defaults.strokeWidth,
  cornerRadius: defaults.cornerRadius,
  inkColor: defaults.inkColor,
  inkWidth: defaults.inkWidth,
  textColor: defaults.textColor,
  fontSize: defaults.fontSize,
})

const GRID_KEY = 'lxcanvas.showGrid'
const [showGrid, setShowGridSignal] = createSignal(localStorage.getItem(GRID_KEY) !== 'false')
export { showGrid }

/** A view preference, not part of the document, so it is kept per browser. */
export function toggleGrid(): void {
  const next = !showGrid()
  setShowGridSignal(next)
  localStorage.setItem(GRID_KEY, String(next))
}
